import {isArray, mapToObj, mapValues, isEmpty, sortBy, isString} from 'remeda'
import {match, P} from 'ts-pattern'
import {ZodRawShape, ZodObject, z, ZodEffects} from 'zod'
import {entries, isNullOrEmpty, mapEntriesToObj} from './collection'

const escapePipe = (value: any) => encodeURIComponent(value).replace(/\|/g, '%7C')

const supportedZodPrimitives = ['ZodNumber', 'ZodString', 'ZodBoolean', 'ZodArray', 'ZodEnum'] as const
type SupportedZodPrimitive = (typeof supportedZodPrimitives)[number]
const isSupportedZodPrimitive = (s: any): s is SupportedZodPrimitive => supportedZodPrimitives.includes(s)

/** A query string serializer with help from zod. Just supports primitives and not nested objects currently */
export const queryStringSerializer = <T extends ZodRawShape, In = any, Out = any>(
  schema: ZodObject<T> | ZodEffects<ZodObject<T>, Out, In>
) => {
  const objectSchema =
    schema._def.typeName === 'ZodEffects' ? (schema._def.schema as ZodObject<T>) : (schema as ZodObject<T>)
  const parse = schema._def.typeName === 'ZodEffects' ? schema.parse : (schema as ZodObject<T>).strip().parse

  const shape = objectSchema.shape
  const propMd = mapEntriesToObj(shape, (name, s: z.ZodTypeAny, i) => {
    // Iterate through nested types, including transformations
    let currentType = s
    while (currentType._def.innerType || currentType._def.typeName === 'ZodEffects')
      currentType = currentType._def.typeName === 'ZodEffects' ? currentType._def.schema : currentType._def.innerType
    const typeName = currentType._def.typeName
    const type = isSupportedZodPrimitive(typeName) ? typeName : undefined
    if (!type) console.warn(`Property ${name} is not a supported primitive type ${typeName}`, s)
    return [name, {type, i}]
  })

  const serialize = (obj: Partial<z.infer<ZodObject<T>>>): string => serializeFromObject(serializeToObject(obj))

  const serializeFromObject = (obj: Record<string, string | undefined>) =>
    sortBy(entries(obj), ([k]) => propMd[k]?.i ?? Number.MAX_SAFE_INTEGER)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')

  const serializeToObject = (obj: Partial<z.infer<ZodObject<T>>>): Record<string, string | undefined> =>
    mapValues(obj, v => (isArray(v) ? v.map(escapePipe).join('|') : v?.toString())) as Record<
      string,
      string | undefined
    >

  const deserializeFromObject = (obj: Record<string, string | undefined | string[]>): z.infer<ZodObject<T>> => {
    const toParse = mapValues(obj, (v, k) => {
      const {type} = propMd[k] ?? {}
      return v == null ? undefined : (
          match(type)
            .with('ZodArray', () =>
              isNullOrEmpty(v) ? []
              : isArray(v) ? v
              : decodeURIComponent(v).split('|')
            )
            .with('ZodNumber', () => (isString(v) ? Number.parseFloat(v) : undefined))
            .with('ZodBoolean', () =>
              match(v)
                .with(P.nullish, () => undefined)
                .with('true', '1', _ => true)
                .with('false', '0', _ => false)
                .otherwise(s => Boolean(s))
            )
            .otherwise(() => (isArray(v) ? v.map(decodeURIComponent) : decodeURIComponent(v)))
        )
    })
    return parse(toParse) as z.infer<ZodObject<T>>
  }

  const deserialize = (query: string) => deserializeFromObject(deserializeToObject(query))

  const deserializeToObject = (query: string) =>
    mapToObj(
      query
        .split('&')
        .map(s => s.split('=').map(decodeURIComponent) as [string, string | undefined])
        .filter(p => p[0] != null),
      ([k, v]) => [k, v]
    )

  return {
    serialize,
    serializeFromObject,
    serializeToObject,
    deserialize,
    deserializeFromObject,
    deserializeToObject,
    propMd
  }
}

export namespace QueryStringUtils {
  export const serializeToObject = (obj: Record<string, string | undefined | string[]>) =>
    mapValues(obj, v => (isArray(v) ? v.map(escapePipe).join('|') : v?.toString())) as Record<
      string,
      string | undefined
    >

  export const queryStringFromObject = (obj: Record<string, string | undefined>) =>
    entries(obj)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')

  export const objectToQueryString = (obj: Record<string, string | undefined>) =>
    entries(obj)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')

  export const queryStringToObject = (query: string) =>
    mapToObj(
      query
        .split('&')
        .map(s => s.split('=').map(decodeURIComponent) as [string, string | undefined])
        .filter(p => p[0] != null),
      ([k, v]) => [k, v]
    )

  let _windowQueryCache: {search: string; result: Record<string, string | undefined>} | undefined
  export const windowQueryObject = (): Record<string, string | undefined> => {
    if (typeof window == 'undefined') return {}
    const search = window.location.search ?? ''
    if (_windowQueryCache?.search == search) return _windowQueryCache.result
    const query = search.startsWith('?') ? search.slice(1) : search
    const result = queryStringToObject(query)
    _windowQueryCache = {search, result}
    return result
  }
}
